import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { toastService } from '@/services/toast.js';
import type { AuthEvent } from '@/types/models.js';
import { acClient } from '@/clients/ac-client.js';

function waitForGoogle(timeoutMs = 10_000) {
    return new Promise<void>((resolve, reject) => {
        if (typeof google !== 'undefined') {
            resolve();
            return;
        }
        const timer = setTimeout(() => {
            clearInterval(interval);
            reject(new Error('Google SDK failed to load'));
        }, timeoutMs);
        const interval = setInterval(() => {
            if (typeof google !== 'undefined') {
                clearInterval(interval);
                clearTimeout(timer);
                resolve();
            }
        }, 50);
    });
}

async function loadConfig() {
    const result = await fetch('/api/config');

    if (!result.ok) {
        throw new Error('Failed to load config');
    }

    return result.json();
}

class AuthService {
    private _accessToken: string | null = null;
    private _idenityProv: string | null = null;
    private _userName: string | null = null;
    private _userType: string | null = null;
    private _expiresAt: number | null = null;
    private _roles: string[] = [];
    private _listeners = new Set<(event: AuthEvent) => void>();
    private _config: any = null;
    private _fireBaseAuth: any = null;
    private _ready: Promise<void>;

    constructor() {
        this._ready = this._init();
        this._ready.catch(() => {}); // error surfaced via toast in _init; suppress unhandled rejection
    }

    async _init() {
        try {
            await waitForGoogle();
            this._config = await loadConfig();
            google.accounts.id.initialize({
                client_id: this._config.googleClientId,
                callback: (response: any) => this._exchangeGoogleResponse(response.credential),
                auto_select: true,
                ux_mode: 'popup',
            });
            const app = initializeApp({
                apiKey:    this._config.firebaseApiKey,
                projectId:  this._config.firebaseProjectId,
            });
            this._fireBaseAuth = getAuth(app);
        } catch (err) {
            toastService.show('Failed to load authentication. Please refresh the page.');
            throw err;
        }
    }

    async initialize() {
        await this._ready;
    }

    _storeToken(token: string) {
        this._accessToken = token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        this._userName = payload.name ?? payload.email ?? payload.sub ?? null;
        this._userType = payload.typ ?? null;
        this._idenityProv = payload.idp ?? null;
        this._expiresAt = payload.exp * 1000;
        this._roles = payload.roles ?? [];
    }

    async _exchangeFirebaseToken(externalToken: string) {
        await this._exchangeExternalToken({ "token_type": "bearer", "firebaseToken": externalToken });
    }

    async _exchangeGoogleResponse(externalToken: string) {
        await this._exchangeExternalToken({ "token_type": "bearer", "googleToken": externalToken });
    }

    async _exchangeExternalToken(payload: Record<string, string>) {
        try {
            const accessResponse = await acClient.authenticate(payload);
            this._storeToken(accessResponse.id_token!);
            this._notify({ status: 'authenticated', accessToken: accessResponse.id_token! });
        } catch (err) {
            toastService.show('Authentication failed. Please try again.');
            this._notify({ status: 'error', error: err as Error });
        }
    }

    async renderSignInButton(element: Element) {
        await this._ready;
        google.accounts.id.renderButton(element, {
            theme: 'outline',
            size: 'large',
            text: 'sign_in_with',
        });
    }

    async signInWithEmail(email: string, password: string) {
        await this._ready;
        try {
            const cred = await signInWithEmailAndPassword(this._fireBaseAuth, email, password);
            const firebaseToken = await cred.user.getIdToken();
            await this._exchangeFirebaseToken(firebaseToken);
        } catch (err) {
            toastService.show('Sign in failed. Please check your credentials.');
            this._notify({ status: 'error', error: err as Error });
        }
    }

    async signOut() {
        await this._ready;

        switch (this._idenityProv) {
            case 'google':
                google.accounts.id.disableAutoSelect();
                break;
        }
        this._accessToken = null;
        this._userName = null;
        this._userType = null;
        this._expiresAt = null;
        this._roles = [];
        this._idenityProv = null;
        try {
            await acClient.logout();
        } finally {
            this._notify({ status: 'unauthenticated' });
        }
    }

    getToken() { return this._accessToken; }
    getUserName() { return this._userName; }
    getUserType() { return this._userType; }
    getRoles() { return this._roles; }
    getIdenityProv() { return this._idenityProv; }

    hasRole(role: string) {
        return this._roles.includes(role);
    }

    async refreshToken() {
        try {
            const accessResponse = await acClient.refreshToken();
            if (!accessResponse) return;
            this._storeToken(accessResponse.id_token!);
            this._notify({ status: 'tokenRefreshed', accessToken: accessResponse.id_token! });
        } catch (err) {
            this._notify({ status: 'error', error: err as Error });
        }
    }

    async ensureValidToken() {
        if (!this.isAuthenticated()) {
            await this.refreshToken();
            if (!this.isAuthenticated()) return;
        }

        if (this._expiresAt !== null && this._expiresAt - Date.now() < 60_000) {
            await this.refreshToken();
        }

        return this.isAuthenticated();
    }

    isAuthenticated() {
        return !!this._accessToken;
    }

    subscribe(listener: (event: AuthEvent) => void) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }

    _notify(event: AuthEvent) {
        this._listeners.forEach(fn => fn(event));
    }
}

export const authService = new AuthService();